import { TextField, TextFieldProps } from '@mui/material';
import {
    formatIncompletePhoneNumber,
    parseIncompletePhoneNumber,
    parsePhoneNumber,
} from 'libphonenumber-js';
import { ReactNode } from 'react';
import { Control, Controller, ControllerProps, FieldError, Path } from 'react-hook-form';
import { useFormError } from 'react-hook-form-mui';
import { FieldValues } from 'react-hook-form/dist/types/fields';

export type TextFieldElementProps<T extends FieldValues = FieldValues> = Omit<
    TextFieldProps,
    'name'
> & {
    validation?: ControllerProps<T>['rules'];
    name: Path<T>;
    parseError?: (error: FieldError) => ReactNode;
    control?: Control<T>;
    component?: typeof TextField;
};

//this component handles formatting and validation of phone numbers
//it does not properly track the cursor position when typing in the middle of a field
//if someone complains enough, algorithms from "input-format" can be used
//see: https://gitlab.com/catamphetamine/input-format
//(this will probably take at least a day of work to implement, if not more)

export default function PhoneNumberTextFieldElement<
    TFieldValues extends FieldValues = FieldValues,
>({
    validation = {},
    parseError,
    required,
    name,
    control,
    component: TextFieldComponent = TextField,
    ...rest
}: TextFieldElementProps<TFieldValues>): JSX.Element {
    const errorMsgFn = useFormError();
    const customErrorFn = parseError || errorMsgFn;
    if (required && !validation.required) {
        validation.required = 'This field is required';
    }

    //isValid() can be swapped for isPossible() depending on user feedback
    if (!validation.validate) {
        validation.validate = (value: string) => {
            try {
                const parsedNumber = parsePhoneNumber(value, 'US');
                return parsedNumber.isValid() ? true : 'Invalid phone number';
            } catch (e: any) {
                return 'Invalid phone number';
            }
        };
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={validation}
            render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => {
                return (
                    <TextFieldComponent
                        {...rest}
                        name={name}
                        value={formatPrettyNumber(value)}
                        onChange={event => {
                            //onChange's value is always formatted as E164: +1##########
                            //rendered "value" is always pretty: (###) ###-####
                            const formatE164 = formatE164Number(event.target.value);
                            onChange(formatE164);
                            event.target.value = formatE164;
                            if (typeof rest.onChange === 'function') {
                                rest.onChange(event);
                            }
                        }}
                        onBlur={onBlur}
                        required={required}
                        type="text"
                        error={!!error}
                        helperText={
                            error
                                ? typeof customErrorFn === 'function'
                                    ? customErrorFn(error)
                                    : error.message
                                : rest.helperText
                        }
                        inputRef={ref}
                    />
                );
            }}
        />
    );
}

function formatE164Number(value: string | undefined) {
    if (!value) return '';

    let result = parseIncompletePhoneNumber(value);

    if (!value.startsWith('+1')) result = '+1' + result;

    return result;
}

function formatPrettyNumber(value: string | undefined) {
    if (!value) return '';

    value = removeCountryPrefix(value);

    let result = formatIncompletePhoneNumber(value, 'US');

    if (result.endsWith(')')) result = result.slice(0, -1);

    return result;
}

function removeCountryPrefix(value: string) {
    //remove leading "+1" in: +1 (###) ###-####
    if (value.startsWith('+1')) {
        value = value.slice(2).trim();
    }
    //remove leading "1" in: 1 (###) ###-####
    if (value.startsWith('1')) {
        value = value.slice(1).trim();
    }
    return value;
}
